import React, { Dispatch, SetStateAction } from 'react';
import _ from 'lodash';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import {
  CategoryChoiceLongHint,
  CategoryChoiceValue,
  CategoryField,
  ChoiceLongHintField,
  FieldSet,
  FormField,
  NumberField,
  SelectField,
  TextCollectionField,
  TextCollectionFieldType as FieldType,
  TextField,
} from '@scale/shared/types/params/textcollection';
import {
  CategoryGraderAnnotation,
  FieldSetGraderAnnotation,
  GraderResult,
  GraderValue,
  isCategoryGraderValue,
  NumberGraderAnnotation,
  SelectGraderAnnotation,
  TextCollectionGraderAnnotation,
  TextCollectionGraderDiffResponse,
  TextGraderAnnotation,
} from '@scale/shared/grader/descriptions/textCollection/graderAnnotationType';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { Breadcrumb, pathJoin } from './breadcrumbs';
import { getFirstTextField } from './fieldSetUtils';
import { Colors } from 'react-scale/components/legacy-css/global_css';
// @ts-ignore import JavaScript package
import pluralize from 'pluralize';
import { TextCollectionUnitValueType } from '@scale/shared/types/response/textcollection';
import apiFetch from 'react-scale/lib/api-fetch';
import { isCategoryField } from '@scale/shared/textcollection/field';
import { fetchLongHints } from '@scale/shared/textcollection/helpers';
import { LongHintContent } from 'react-scale/components/scalers/tasks/textcollection/CategoryFieldLongHints';
import { useUnleashBoolean } from 'react-scale/hooks/useUnleashFeatureFlag';

import { SectionHeader } from '@scale/scaleui';

enum DiffPalette {
  Correct = '#29CD9C',
  Incorrect = '#D4354F',
  AnswerKey = '#4767D0',
}

interface DiffTreeProps {
  fields: TextCollectionField[];
  response: TextCollectionGraderDiffResponse;
  showEmptyDiffMessage?: boolean;
  isCompToPrevious?: boolean;
  initialExpandedNodeIds: string[];
  projectId?: string;
}

export default function DiffTree(props: DiffTreeProps) {
  const classes = useDiffTreeStyles();
  const [expanded, setExpanded] = React.useState<string[]>(props.initialExpandedNodeIds);
  const onNodeToggle = (event: any, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };
  const breadcrumbs: Breadcrumb[] = [];

  const isCompToPrevious = props.isCompToPrevious || false;

  let diffContent;
  if (props.showEmptyDiffMessage && isEmptyTree(props.fields, props.response.annotations)) {
    diffContent = <div className={classes.emptyLabel}>No parts of this question were graded.</div>;
  } else {
    diffContent = (
      <FieldListDiff
        breadcrumbs={breadcrumbs}
        fields={props.fields}
        annotations={props.response.annotations}
        fieldSetGraderResult={GraderResult.Matched}
        isCompToPrevious={isCompToPrevious}
        projectId={props.projectId}
      />
    );
  }

  return (
    <TreeView expanded={expanded} onNodeToggle={onNodeToggle}>
      <div className={classes.legend}>
        <div
          className={classNames(classes.swatch, { [classes.opaque]: isCompToPrevious })}
          style={{ background: DiffPalette.Correct }}
        />
        <div className={classNames(classes.swatchLabel, { [classes.opaque]: isCompToPrevious })}>
          {isCompToPrevious ? 'Unchanged' : 'Correct'}
        </div>
        <div className={classes.swatch} style={{ background: DiffPalette.Incorrect }} />
        <div className={classes.swatchLabel}>{isCompToPrevious ? 'Edited' : 'Incorrect'}</div>
        <div className={classes.swatch} style={{ background: DiffPalette.AnswerKey }} />
        <div className={classes.swatchLabel}>{isCompToPrevious ? 'Added' : 'Answer Key'}</div>
      </div>
      <div className={classes.topLevelGroup}>{diffContent}</div>
    </TreeView>
  );
}

DiffTree.defaultProps = {
  showEmptyDiffMessage: false,
  initialExpandedNodeIds: [],
};

interface FieldListDiffProps {
  breadcrumbs: Breadcrumb[];
  fields: TextCollectionField[];
  annotations: Record<string, TextCollectionGraderAnnotation>;
  fieldSetGraderResult: GraderResult;
  isCompToPrevious: boolean;
  projectId?: string;
}

function FieldListDiff(props: FieldListDiffProps) {
  const fieldDiffs = props.fields
    .map(field => {
      const annotation = props.annotations[field.field_id];
      // when annotation is undefined, it means this field
      // does not exist in either given or expected response
      return (
        !!annotation && (
          <FieldDiff
            key={field.field_id}
            breadcrumbs={props.breadcrumbs}
            field={field}
            annotation={annotation}
            fieldSetGraderResult={props.fieldSetGraderResult}
            isCompToPrevious={props.isCompToPrevious}
            projectId={props.projectId}
          />
        )
      );
    })
    .filter(Boolean);
  return <>{fieldDiffs}</>;
}

interface FieldDiffProps {
  breadcrumbs: Breadcrumb[];
  field: TextCollectionField;
  annotation: TextCollectionGraderAnnotation;
  fieldSetGraderResult: GraderResult;
  isCompToPrevious: boolean;
  projectId?: string;
}

function FieldDiff(props: FieldDiffProps) {
  if (props.field.type === FieldType.FieldSet && props.annotation.type === FieldType.FieldSet) {
    return (
      <FieldSetDiff
        breadcrumbs={props.breadcrumbs}
        field={props.field}
        annotation={props.annotation}
        fieldSetGraderResult={props.fieldSetGraderResult}
        isCompToPrevious={props.isCompToPrevious}
      />
    );
  }
  if (props.field.type === FieldType.Form && props.annotation.type === FieldType.FieldSet) {
    return (
      <FormFieldDiff
        breadcrumbs={props.breadcrumbs}
        field={props.field}
        annotation={props.annotation}
        fieldSetGraderResult={props.fieldSetGraderResult}
        isCompToPrevious={props.isCompToPrevious}
      />
    );
  }
  if (
    (props.field.type === FieldType.Text && props.annotation.type === FieldType.Text) ||
    (props.field.type === FieldType.Number && props.annotation.type === FieldType.Number) ||
    (props.field.type === FieldType.Category && props.annotation.type === FieldType.Category) ||
    (props.field.type === FieldType.Select && props.annotation.type === FieldType.Select)
  ) {
    return (
      <UnitFieldDiff
        field={props.field}
        annotation={props.annotation}
        fieldSetGraderResult={props.fieldSetGraderResult}
        isCompToPrevious={props.isCompToPrevious}
        projectId={props.projectId}
      />
    );
  }
  return null;
}

interface FieldSetDiffProps {
  breadcrumbs: Breadcrumb[];
  field: FieldSet;
  annotation: FieldSetGraderAnnotation;
  fieldSetGraderResult: GraderResult;
  isCompToPrevious: boolean;
}

function FieldSetDiff(props: FieldSetDiffProps) {
  const classes = useDiffTreeStyles();
  const count = props.annotation.response.length;
  if (!count) {
    return null;
  }
  const isParentMatched = props.fieldSetGraderResult === GraderResult.Matched;
  const title = pluralize(props.field.title.toLowerCase(), count, false);
  return (
    <div>
      <div className={classes.fieldSetCount}>
        {count} {title}:
      </div>
      {props.annotation.response.map((response, index) => {
        const breadcrumbs = [...props.breadcrumbs, { id: props.field.field_id, index }];
        const nodeId = pathJoin(breadcrumbs);
        const label = getFieldSetLabel(props.field, response) ?? 'Entry ' + index;
        const annotations = getValue(response);
        return (
          <TreeItem
            key={nodeId}
            nodeId={nodeId}
            label={label}
            classes={{
              root: classes.root,
              expanded: classes.expanded,
              label: classNames(classes.fieldSetLabel, {
                [classes.strike]: response.result === GraderResult.Extraneous,
                [classes.opaque]:
                  props.isCompToPrevious &&
                  response.result === GraderResult.Matched &&
                  response.hasCorrectChildren,
              }),
              content: classNames(classes.fieldSetContent, {
                [classes.fieldSetIncorrect]:
                  isParentMatched && response.result === GraderResult.Extraneous,
                [classes.fieldSetAnswerKey]:
                  isParentMatched && response.result === GraderResult.Missing,
              }),
              group: classes.fieldSetGroup,
              iconContainer: classNames(classes.iconContainer, {
                [classes.opaque]:
                  props.isCompToPrevious &&
                  response.result === GraderResult.Matched &&
                  response.hasCorrectChildren,
                [classes.iconCorrect]:
                  response.result === GraderResult.Matched && response.hasCorrectChildren,
                [classes.iconIncorrect]:
                  response.result === GraderResult.Extraneous ||
                  (response.result === GraderResult.Matched && !response.hasCorrectChildren),
                [classes.iconAnswerKey]: response.result === GraderResult.Missing,
              }),
            }}
            expandIcon={<TriangleRight />}
            collapseIcon={<TriangleDown />}
          >
            <FieldListDiff
              breadcrumbs={breadcrumbs}
              fields={props.field.fields}
              annotations={annotations}
              fieldSetGraderResult={response.result}
              isCompToPrevious={props.isCompToPrevious}
            />
          </TreeItem>
        );
      })}
    </div>
  );
}

interface FormFieldDiffProps {
  breadcrumbs: Breadcrumb[];
  field: FormField;
  annotation: FieldSetGraderAnnotation;
  fieldSetGraderResult: GraderResult;
  isCompToPrevious: boolean;
}

function FormFieldDiff(props: FormFieldDiffProps) {
  const classes = useDiffTreeStyles();
  const count = props.annotation.response.length;
  if (!count) {
    return null;
  }
  const isParentMatched = props.fieldSetGraderResult === GraderResult.Matched;
  return (
    <div>
      {props.annotation.response.map((response, index) => {
        const breadcrumbs = [...props.breadcrumbs, { id: props.field.field_id, index }];
        const nodeId = pathJoin(breadcrumbs);
        const label = props.field.title;
        const annotations = getValue(response);
        return (
          <TreeItem
            key={nodeId}
            nodeId={nodeId}
            label={label}
            classes={{
              root: classes.root,
              expanded: classes.expanded,
              label: classNames(classes.fieldSetLabel, {
                [classes.strike]: response.result === GraderResult.Extraneous,
                [classes.opaque]:
                  props.isCompToPrevious &&
                  response.result === GraderResult.Matched &&
                  response.hasCorrectChildren,
              }),
              content: classNames(classes.fieldSetContent, {
                [classes.fieldSetIncorrect]:
                  isParentMatched && response.result === GraderResult.Extraneous,
                [classes.fieldSetAnswerKey]:
                  isParentMatched && response.result === GraderResult.Missing,
              }),
              group: classes.fieldSetGroup,
              iconContainer: classNames(classes.iconContainer, {
                [classes.opaque]:
                  props.isCompToPrevious &&
                  response.result === GraderResult.Matched &&
                  response.hasCorrectChildren,
                [classes.iconCorrect]:
                  response.result === GraderResult.Matched && response.hasCorrectChildren,
                [classes.iconIncorrect]:
                  response.result === GraderResult.Extraneous ||
                  (response.result === GraderResult.Matched && !response.hasCorrectChildren),
                [classes.iconAnswerKey]: response.result === GraderResult.Missing,
              }),
            }}
            expandIcon={<TriangleRight />}
            collapseIcon={<TriangleDown />}
          >
            <FieldListDiff
              breadcrumbs={breadcrumbs}
              fields={props.field.fields}
              annotations={annotations}
              fieldSetGraderResult={response.result}
              isCompToPrevious={props.isCompToPrevious}
            />
          </TreeItem>
        );
      })}
    </div>
  );
}

function getFieldSetLabel(
  field: FieldSet,
  graderValue: GraderValue<Record<string, TextCollectionGraderAnnotation>>,
): string | undefined {
  const firstTextField = getFirstTextField(field);
  if (!firstTextField) {
    return;
  }
  const fieldSetResponse = getValue(graderValue);
  const firstTextFieldAnnotation = fieldSetResponse[firstTextField.field_id];
  if (!firstTextFieldAnnotation || firstTextFieldAnnotation.type !== FieldType.Text) {
    return '';
  }
  return getValue(firstTextFieldAnnotation.response[0]);
}

function getValue<T>(graderValue: GraderValue<T>): T {
  const value: any = graderValue;
  if (value.expected !== undefined) {
    return value.expected;
  }
  return value.given;
}

interface UnitFieldDiffProps {
  field: TextField | NumberField | CategoryField | SelectField;
  annotation:
    | TextGraderAnnotation
    | NumberGraderAnnotation
    | CategoryGraderAnnotation
    | SelectGraderAnnotation;
  fieldSetGraderResult: GraderResult;
  isCompToPrevious: boolean;
  projectId?: string;
}

function getValueString(value: TextCollectionUnitValueType): string {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.join(' | ');
  }
  return String(value);
}

function getCategoryGraderValue(
  graderValue: GraderValue<CategoryChoiceValue[]>,
  type: 'given' | 'expected',
  longHintInformation?: LongHintInformation,
) {
  const choiceValues: CategoryChoiceValue[] = _.get(graderValue, type);
  const { fieldLongHints, setLongHints } = longHintInformation ?? {};

  if (choiceValues.length === 1) {
    const valueString = String(choiceValues[0]);
    const allLabels = _.get(graderValue, ['categoryMetadata', type, valueString, 'labels']);
    return (
      <>
        {getValueString(allLabels?.length > 0 ? allLabels : valueString)}
        {fieldLongHints && fieldLongHints[valueString] && setLongHints && (
          <a style={{ fontWeight: 100 }} onClick={() => setLongHints(fieldLongHints[valueString])}>
            {' '}
            (See Instructions)
          </a>
        )}
      </>
    );
  }

  return choiceValues.map(value => {
    const valueString = String(value);
    const allLabels = _.get(graderValue, ['categoryMetadata', type, valueString, 'labels']);
    return (
      <div key={valueString}>
        {'- '}
        {getValueString(allLabels ?? valueString)}
        {fieldLongHints && fieldLongHints[valueString] && setLongHints && (
          <a style={{ fontWeight: 100 }} onClick={() => setLongHints(fieldLongHints[valueString])}>
            {' '}
            (See Instructions)
          </a>
        )}
      </div>
    );
  });
}

interface LongHintInformation {
  fieldLongHints: Record<string, CategoryChoiceLongHint[]>;
  setLongHints: Dispatch<SetStateAction<CategoryChoiceLongHint[]>>;
}

function getGraderValue(
  graderValue: GraderValue<TextCollectionUnitValueType>,
  type: 'given' | 'expected',
  longHintInformation?: LongHintInformation,
) {
  const value = _.get(graderValue, type) as TextCollectionUnitValueType;
  if (value === undefined) {
    return null;
  } else if (isCategoryGraderValue(graderValue)) {
    return getCategoryGraderValue(
      graderValue as GraderValue<CategoryChoiceValue[]>,
      type,
      longHintInformation,
    );
  } else {
    return getValueString(value);
  }
}

function UnitFieldDiff(props: UnitFieldDiffProps) {
  const count = props.annotation.response.length;

  const [fieldLongHints, setFieldLongHints] = React.useState<
    Record<string, CategoryChoiceLongHint[]>
  >({});
  const [longHints, setLongHints] = React.useState<CategoryChoiceLongHint[]>([]);

  const unleashUseOntologyHints = useUnleashBoolean(
    'use-ontology-hints',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  React.useEffect(() => {
    setFieldLongHints({});
    if (!isCategoryField(props.field)) {
      return;
    }
    const fetchLongHintsFromOntology = async () => {
      try {
        const { longHints } = await apiFetch(
          null,
          `/internal/ontology_manager/get_long_hints_from_ontology/${props.projectId}`,
          {
            method: 'GET',
          },
        );
        setFieldLongHints(longHints);
      } catch (error) {
        return;
      }
    };
    if (props.field.useOntologyHints || unleashUseOntologyHints) {
      fetchLongHintsFromOntology();
    } else if (props.field.longHintsS3Artifact) {
      fetchLongHints(props.field.longHintsS3Artifact, setFieldLongHints);
    }
  }, [props.field, props.projectId, unleashUseOntologyHints]);

  const hintHasContent = (longhint: ChoiceLongHintField) => {
    return !(_.isEmpty(longhint.text?.trim()) && _.isEmpty(longhint.imageUrl?.trim()));
  };

  if (!count) {
    return null;
  }
  const multiple = count > 1;
  const graderValues = props.annotation.response as GraderValue<TextCollectionUnitValueType>[];

  const responses = graderValues.map((value, index) => {
    const key = `${props.field.field_id}[${index}]`;
    return (
      <div key={key}>
        {(value.result === GraderResult.Correct ||
          value.result === GraderResult.Extraneous ||
          value.result === GraderResult.Incorrect) && (
          <FieldResponse
            title={props.field.title}
            index={index}
            multiple={multiple}
            variant={value.result === GraderResult.Correct ? 'correct' : 'incorrect'}
            fieldSetGraderResult={props.fieldSetGraderResult}
            isCompToPrevious={props.isCompToPrevious}
          >
            {getGraderValue(value, 'given', { fieldLongHints, setLongHints })}
          </FieldResponse>
        )}
        {(value.result === GraderResult.Missing || value.result === GraderResult.Incorrect) && (
          <FieldResponse
            title={props.field.title}
            index={index}
            multiple={multiple}
            variant="answer"
            fieldSetGraderResult={props.fieldSetGraderResult}
            isCompToPrevious={props.isCompToPrevious}
          >
            {getGraderValue(value, 'expected', { fieldLongHints, setLongHints })}
          </FieldResponse>
        )}
      </div>
    );
  });
  return (
    <>
      {responses}
      {longHints.length ? (
        <div style={{ marginTop: 20 }}>
          <LongHintContent
            responsive={false}
            hintHasContent={hintHasContent}
            longHints={longHints}
          />
        </div>
      ) : (
        <></>
      )}
    </>
  );
}

interface FieldResponseProps {
  title: string;
  index: number;
  multiple: boolean;
  variant: 'correct' | 'incorrect' | 'answer';
  fieldSetGraderResult: GraderResult;
  isCompToPrevious: boolean;
}

function FieldResponse(props: React.PropsWithChildren<FieldResponseProps>) {
  const classes = useDiffTreeStyles();
  const label = getFieldLabel(props.title, props.index, props.multiple);
  const isParentMatched = props.fieldSetGraderResult === GraderResult.Matched;

  const descriptionJSX = (
    <div className={classes.textbox}>
      <span
        className={classNames({
          [classes.opaque]:
            props.isCompToPrevious && isParentMatched && props.variant === 'correct',
          [classes.correct]: !isParentMatched || props.variant === 'correct',
          [classes.incorrect]: isParentMatched && props.variant === 'incorrect',
          [classes.answer]: isParentMatched && props.variant === 'answer',
        })}
      >
        {props.children}
      </span>
    </div>
  );
  return (
    <div className={classes.response}>
      <SectionHeader title={label} description={descriptionJSX} />
    </div>
  );
}

function getFieldLabel(title: string, index: number, multiple: boolean): string {
  const suffix = multiple ? `[${index + 1}]` : '';
  return `${title}${suffix}:`;
}

const useDiffTreeStyles = makeStyles({
  legend: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  swatch: {
    width: 16,
    height: 16,
    borderRadius: 4,
    marginRight: 8,
  },
  swatchLabel: {
    fontSize: '14px',
    lineHeight: '20px',
    marginRight: 16,
    color: '#000000',
  },
  response: {
    fontSize: '14px',
    lineHeight: '18px',
    padding: '8px',
  },
  label: {
    color: Colors.RemoGray50,
  },
  emptyLabel: {
    fontSize: 14,
    lineHeight: '20px',
    color: '#808080',
  },
  correct: {
    color: '#000000',
  },
  incorrect: {
    color: DiffPalette.Incorrect,
    fontWeight: 500,
  },
  answer: {
    color: DiffPalette.AnswerKey,
    fontWeight: 500,
  },
  fieldSetCount: {
    fontSize: '14px',
    lineHeight: '18px',
    color: Colors.RemoGray50,
    padding: 8,
  },
  fieldSetLabel: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '18px',
    color: '#000000',
  },
  fieldSetContent: {
    padding: 8,
    borderRadius: 4,
  },
  fieldSetGroup: {
    borderLeft: `1px dashed ${Colors.RemoGray50}`,
    paddingLeft: 8,
    paddingTop: 8,
    paddingBottom: 8,
    marginLeft: 16,
  },
  iconContainer: {
    width: 16,
    marginRight: 8,
  },
  iconCorrect: {
    color: DiffPalette.Correct,
  },
  iconIncorrect: {
    color: DiffPalette.Incorrect,
  },
  iconAnswerKey: {
    color: DiffPalette.AnswerKey,
  },
  fieldSetIncorrect: {
    background: rgba(DiffPalette.Incorrect, 0.2),
    '&:hover': {
      background: rgba(DiffPalette.Incorrect, 0.3),
    },
  },
  fieldSetAnswerKey: {
    background: rgba(DiffPalette.AnswerKey, 0.2),
    '&:hover': {
      background: rgba(DiffPalette.AnswerKey, 0.3),
    },
  },
  topLevelGroup: {
    paddingLeft: 8,
    marginTop: 24,
  },
  root: {},
  expanded: {
    '& > $fieldSetContent > $iconContainer': {
      color: Colors.RemoGray50,
    },
  },
  strike: {
    textDecoration: 'line-through',
  },
  opaque: {
    opacity: 0.25,
  },
  textbox: {
    boxSizing: 'border-box',
    border: 'solid rgb(188, 188, 188)',
    borderRadius: 10,
    padding: '1vh',
  },
});

function isEmptyTree(
  fields: TextCollectionField[],
  annotations: Record<string, TextCollectionGraderAnnotation>,
): boolean {
  for (const field of fields) {
    if (annotations[field.field_id]) {
      return false;
    }
  }
  return true;
}

function TriangleRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M13.7999 9.04011C14.5999 8.58011 14.5999 7.42261 13.7999 6.96011L3.3999 0.962609C2.6024 0.502609 1.5999 1.07761 1.5999 2.00261L1.5999 14.0001C1.5999 14.9226 2.5999 15.5001 3.3999 15.0401L13.7999 9.04011Z" />
    </svg>
  );
}

function TriangleDown() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M6.95989 13.8004C7.41989 14.6004 8.57739 14.6004 9.03989 13.8004L15.0374 3.40039C15.4974 2.60289 14.9224 1.60039 13.9974 1.60039L1.99989 1.60039C1.07739 1.60039 0.499891 2.60039 0.959891 3.40039L6.95989 13.8004Z" />
    </svg>
  );
}

function rgba(hex: string, alpha: number): string {
  const color = hex.replace('#', '');
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
